// src/components/ui/Table/TableHeader.tsx
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { TableColumn } from './Table';
import styles from './TableHeader.module.css';

interface TableHeaderProps<T> {
  columns: TableColumn<T>[];
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onColumnResize?: (columnKey: string, width: number) => void;
  columnWidths?: Record<string, number>;
  containerWidth?: number; // Ширина контейнера для адаптивности
  responsive?: boolean; // Флаг адаптивности
  compactMode?: boolean; // Новый параметр для компактного режима на мобильных
}

export function TableHeader<T>({ 
  columns,
  sortColumn,
  sortDirection,
  onSort,
  onColumnResize,
  columnWidths = {},
  containerWidth = 0,
  responsive = true,
  compactMode = false // По умолчанию выключен
}: TableHeaderProps<T>) {
  // Используем хук для переводов
  const { t } = useTranslation('ui');
  
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [startWidth, setStartWidth] = useState<number>(0);
  const headerRefs = useRef<Record<string, HTMLElement | null>>({});

  // Отслеживаем движение курсора при изменении размера столбца
  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: any) => {
      if (!resizingColumn || !startWidth) return;
      
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(80, startWidth + deltaX); // Минимальная ширина столбца 80px
      
      if (onColumnResize) {
        onColumnResize(resizingColumn, newWidth);
      }
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    // Добавляем слушатели событий с проверкой для обеспечения TypeScript-совместимости
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [resizingColumn, startX, startWidth, onColumnResize]);

  // Обработчик для сортировки столбца
  const handleSortClick = (column: TableColumn<T>) => {
    if (!column.sortable || !onSort) return;
    
    onSort(column.key);
  };

  // Обработчик начала изменения размера столбца
  const handleResizeStart = (columnKey: string, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    
    const currentHeader = headerRefs.current[columnKey];
    if (currentHeader) {
      // Используем любой тип для доступа к offsetWidth
      setStartWidth((currentHeader as any).offsetWidth || 0);
    }
  };

  // Функция для расчета ширины столбца с учетом адаптивности
  const getColumnWidth = (column: TableColumn<T>): React.CSSProperties => {
    // Ширина из сохраненных настроек
    const savedWidth = columnWidths[column.key as string];
    
    // Если есть сохраненное значение, используем его
    if (savedWidth) {
      return {
        width: `${savedWidth}px`,
        minWidth: `${savedWidth}px`
      };
    }
    
    // Для компактного режима на мобильных используем меньшие размеры
    const mobileScale = compactMode ? 0.7 : 1;
    
    // Значения по умолчанию, если нет адаптивности или не задана ширина контейнера
    if (!responsive || !containerWidth) {
      return {
        minWidth: column.width === 'xs' ? `${60 * mobileScale}px` :
                column.width === 'sm' ? `${100 * mobileScale}px` :
                column.width === 'md' ? `${150 * mobileScale}px` :
                column.width === 'lg' ? `${200 * mobileScale}px` :
                column.width === 'xl' ? `${250 * mobileScale}px` :
                column.width === '2xl' ? `${300 * mobileScale}px` :
                column.width === '3xl' ? `${350 * mobileScale}px` : `${100 * mobileScale}px`,
      };
    }
    
    // Расчет ширины для адаптивных столбцов
    // Вес столбца в зависимости от размера
    const weightMap: Record<string, number> = {
      'xs': 1,
      'sm': 2,
      'md': 3,
      'lg': 4,
      'xl': 5,
      '2xl': 6,
      '3xl': 7,
      'auto': 3 // Значение по умолчанию
    };
    
    // Рассчитываем общий вес всех столбцов
    const totalWeight = columns.reduce((sum, col) => {
      const weight = weightMap[col.width || 'auto'] || 3;
      return sum + weight;
    }, 0);
    
    // Вес текущего столбца
    const columnWeight = weightMap[column.width || 'auto'] || 3;
    
    // Минимальная ширина столбца (можно настроить)
    const minWidth = column.minWidth || (compactMode ? 40 : 60);
    
    // Рассчитываем пропорциональную ширину столбца с учетом компактности
    const calculatedWidth = Math.max(
      minWidth,
      (containerWidth * columnWeight * mobileScale) / totalWeight
    );
    
    return {
      width: `${calculatedWidth}px`,
      minWidth: `${minWidth}px`
    };
  };

  // Определяем классы для заголовка
  const getHeaderCellClass = (column: TableColumn<T>) => {
    // Определяем выравнивание
    const alignClass = column.textAlign === 'center' 
      ? styles.cellCenter 
      : column.textAlign === 'right' 
        ? styles.cellRight 
        : styles.cellLeft;
    
    // Определяем класс для сортировки
    const sortableClass = column.sortable ? styles.sortable : '';
    const sortedClass = column.sortable && sortColumn === column.key ? styles.sorted : '';
    
    // Определяем класс для компактного режима
    const compactClass = compactMode ? styles.compact : '';
    
    return `${styles.headerCell} ${alignClass} ${sortableClass} ${sortedClass} ${compactClass}`;
  };

  return (
    <thead className={styles.tableHeader}>
      <tr>
        {columns.map((column) => (
          <th 
            key={column.key as string}
            ref={(el) => headerRefs.current[column.key as string] = el}
            className={getHeaderCellClass(column)}
            style={getColumnWidth(column)}
            onClick={column.sortable ? () => handleSortClick(column) : undefined}
          >
            <div className={styles.headerContent}>
              <span className={styles.headerTitle}>
                {column.title}
              </span>
              {column.sortable && sortColumn === column.key && (
                <span className={styles.sortIcon}>
                  {sortDirection === 'asc' ? (
                    <ChevronUp className={compactMode ? styles.compactIcon : ''} />
                  ) : (
                    <ChevronDown className={compactMode ? styles.compactIcon : ''} />
                  )}
                </span>
              )}
            </div>
            
            {/* Элемент для изменения размера столбца */}
            {column.resizable !== false && onColumnResize && !compactMode && (
              <div
                className={`${styles.resizer} ${resizingColumn === column.key ? styles.resizerActive : ''}`}
                onMouseDown={(e) => handleResizeStart(column.key as string, e)}
                title={t('table.resize.dragToResize')}
              >
                <div className={styles.resizerLine}></div>
              </div>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );
}